import { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { appState } from "../../../state";
import { useAppState } from "../../hooks/useAppState";
import { getDirectory } from "../../../mentions";
import { formatWatchTogetherCountdown, formatRelativeTime } from "../../../utils";
import { mediaUrl } from "../../../mediaAuth";
import {
  cancelTogetherScheduleApi,
  fetchTogetherHistoryApi,
  inviteTogetherApi,
  scheduleTogetherApi,
  updateTogetherScheduleApi,
} from "../../../togetherApi";
import type { TogetherMediaType, TogetherSchedule, TogetherSessionHistoryEntry } from "../../../types";

// "12 min" / "45 sec" - duration is a nicety, not worth a shared utils.ts
// helper for its one use site.
function formatDuration(startedAt: string, endedAt: string): string {
  const ms = Math.max(0, Date.parse(endedAt) - Date.parse(startedAt));
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} sec`;
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

// Thumbnails are image-only server-side (/assets/thumbs 404s for video
// extensions) - falls back to a generic icon for videos and for any
// thumbnail that fails to load (source file since renamed/deleted).
function HistoryThumb({ entry }: { entry: TogetherSessionHistoryEntry }) {
  const [failed, setFailed] = useState(false);
  if (entry.mediaType === "video" || failed) {
    return (
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 40, height: 40, borderRadius: 8, background: "var(--surface-soft)" }}
      >
        <i className={`bi ${entry.mediaType === "video" ? "bi-camera-video-fill" : "bi-image-fill"} text-pink`} aria-hidden="true" />
      </span>
    );
  }
  return (
    <img
      src={mediaUrl(`/assets/thumbs/${entry.filename}`)}
      alt=""
      onError={() => setFailed(true)}
      style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
    />
  );
}

// The "given a video, invite now or schedule" engine - extracted from what
// used to be TogetherInviteDialog.tsx's entire <DialogContent> body, so
// the dedicated /watch-together page (WatchTogetherScreen.tsx) and the
// still-existing modal (TogetherInviteDialog.tsx, used when a video is
// already open in the Lightbox) share one real implementation instead of
// two. Both hosts remount this component on open/selection-change (a
// conditional render or a `key` prop) rather than this component tracking
// its own "open" prop - that's what resets mode/error/date/recipient state
// between uses, replacing the dialog's old internal reset effect.
//
// When `filename` is null, the actual invite/schedule form doesn't render
// at all (a proactive guard, not just the old submit-time-only error) -
// Upcoming and Past Sessions still do, since neither needs a filename.
export function WatchTogetherForm({
  filename,
  mediaType,
  onDone,
  onBusyChange,
}: {
  filename: string | null;
  mediaType: TogetherMediaType;
  onDone: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const togetherSchedules = useAppState((s) => s.togetherSchedules);
  const recipients = getDirectory().filter((u) => u.email !== appState.currentUserEmail);
  const needsRecipientPicker = recipients.length > 1;
  const soleRecipient = recipients.length === 1 ? recipients[0] : null;

  const [mode, setMode] = useState<"now" | "schedule">("now");
  // Not initialized from soleRecipient in a mount effect: loadDirectory()
  // is one of several unawaited parallel calls in AuthGate's
  // startProtectedFeatures(), so the directory can still be empty at the
  // instant this form first renders. Deriving the fallback at render time
  // instead means it's always correct once the directory arrives, with no
  // dependency on effect timing (same staleness class as the
  // reconnect-resync bugs fixed elsewhere in Together Mode/Game).
  const [recipientEmailChoice, setRecipientEmail] = useState("");
  const recipientEmail = recipientEmailChoice || soleRecipient?.email || "";
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [busyScheduleId, setBusyScheduleId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<TogetherSessionHistoryEntry[] | null>(null);

  useEffect(() => {
    onBusyChange?.(sending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError("");

    if (!filename) {
      // Defensive only at this point - the form itself doesn't render when
      // filename is null (see below), so submit can't actually be reached
      // without one.
      setError("Nothing selected to watch together.");
      return;
    }
    if (!recipientEmail) {
      setError("Choose who to invite.");
      return;
    }

    if (mode === "schedule") {
      if (mediaType !== "video") {
        setError("Only videos can be scheduled.");
        return;
      }
      if (!startsAtLocal) {
        setError("Choose a date and time.");
        return;
      }
      // <input type="datetime-local">'s value has no timezone suffix, so
      // the Date constructor interprets it in the browser's own local
      // timezone - .toISOString() then converts to UTC for the server,
      // satisfying "store UTC, display local" with no date library.
      const startsAtIso = new Date(startsAtLocal).toISOString();
      if (Date.parse(startsAtIso) <= Date.now()) {
        setError("Choose a time in the future.");
        return;
      }

      setSending(true);
      const result = await scheduleTogetherApi({ recipientEmail, filename, startsAt: startsAtIso });
      setSending(false);
      if (!result.ok) {
        setError(result.error || "Failed to schedule.");
        return;
      }
      onDone();
      return;
    }

    setSending(true);
    const result = await inviteTogetherApi({ recipientEmail, filename, mediaType });
    setSending(false);

    if (!result.ok) {
      setError(result.error || "Failed to send invite.");
      return;
    }

    onDone();
  }

  function startEdit(schedule: TogetherSchedule): void {
    setEditingId(schedule.id);
    // toISOString() is UTC ("...Z") - datetime-local inputs need
    // "YYYY-MM-DDTHH:mm" with no suffix, in LOCAL time, so build it from
    // the Date object's local getters rather than string-slicing the ISO.
    const d = new Date(schedule.startsAt);
    const pad = (n: number) => String(n).padStart(2, "0");
    setEditValue(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }

  async function saveEdit(scheduleId: string): Promise<void> {
    if (!editValue) return;
    setBusyScheduleId(scheduleId);
    const result = await updateTogetherScheduleApi(scheduleId, new Date(editValue).toISOString());
    setBusyScheduleId(null);
    if (result.ok) setEditingId(null);
    else setError(result.error || "Failed to update.");
  }

  async function cancelSchedule(scheduleId: string): Promise<void> {
    setBusyScheduleId(scheduleId);
    await cancelTogetherScheduleApi(scheduleId);
    setBusyScheduleId(null);
  }

  // Lazily fetched, once - past sessions don't change once written (no push
  // event to react to), so there's no reason to load this before the user
  // actually wants to see it.
  async function toggleHistory(): Promise<void> {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) {
      setHistoryLoading(true);
      setHistory(await fetchTogetherHistoryApi());
      setHistoryLoading(false);
    }
  }

  const upcoming = togetherSchedules.filter((s) => s.status === "scheduled" || s.status === "ready");

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {filename ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant={mode === "now" ? "contained" : "outlined"}
              color="primary"
              onClick={() => setMode("now")}
              disabled={sending}
              sx={{ flex: 1 }}
            >
              Watch Now
            </Button>
            <Button
              size="small"
              variant={mode === "schedule" ? "contained" : "outlined"}
              color="primary"
              onClick={() => setMode("schedule")}
              disabled={sending || mediaType !== "video"}
              sx={{ flex: 1 }}
            >
              Schedule
            </Button>
          </Box>
          {mode === "schedule" && mediaType !== "video" && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Only videos can be scheduled - photos are watch-now only.
            </Alert>
          )}

          {mode === "schedule" && mediaType === "video" && (
            <TextField
              label="Date & time"
              type="datetime-local"
              value={startsAtLocal}
              onChange={(e) => setStartsAtLocal(e.target.value)}
              fullWidth
              size="small"
              disabled={sending}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}

          {needsRecipientPicker && (
            <TextField
              select
              label="Invite"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              fullWidth
              size="small"
              disabled={sending}
            >
              {recipients.map((u) => (
                <MenuItem key={u.email} value={u.email}>
                  {u.fullName || u.email}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!recipients.length && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No one else to invite yet.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          <Button type="submit" variant="contained" color="primary" disabled={sending || !recipients.length} aria-label="Send invite">
            <i className="bi bi-people-fill me-2" aria-hidden="true" style={{ marginRight: 8 }} />
            {sending ? (mode === "schedule" ? "Scheduling…" : "Inviting…") : mode === "schedule" ? "Schedule Watch Together" : "Invite to Watch"}
          </Button>
        </Box>
      ) : (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Open a video to start watching together.
        </Alert>
      )}

      {upcoming.length > 0 && (
        <>
          <Divider />
          <Box className="small fw-semibold">Upcoming</Box>
          <Box sx={{ display: "grid", gap: 1 }}>
            {upcoming.map((s) => (
              <Box
                key={s.id}
                sx={{
                  p: 1,
                  borderRadius: "var(--radius-sm, 12px)",
                  border: "1px solid var(--surface-border)",
                  background: "var(--surface-soft)",
                }}
              >
                <Box className="d-flex align-items-center justify-content-between">
                  <Box>
                    <Box className="small fw-semibold">With {s.peerName}</Box>
                    <Box className="small muted">{s.status === "ready" ? "Ready to Watch" : formatWatchTogetherCountdown(s.startsAt)}</Box>
                  </Box>
                  {s.isCreator && editingId !== s.id && (
                    <Box className="d-flex gap-1">
                      <IconButton size="small" aria-label="Edit schedule" onClick={() => startEdit(s)} disabled={busyScheduleId === s.id}>
                        <i className="bi bi-pencil" aria-hidden="true" />
                      </IconButton>
                      <IconButton size="small" aria-label="Cancel schedule" onClick={() => cancelSchedule(s.id)} disabled={busyScheduleId === s.id}>
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {editingId === s.id && (
                  <Box className="d-flex align-items-center gap-1 mt-1">
                    <TextField
                      type="datetime-local"
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Button size="small" onClick={() => saveEdit(s.id)} disabled={busyScheduleId === s.id}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}

      <Divider />
      <Button size="small" onClick={toggleHistory} sx={{ justifyContent: "space-between" }} aria-expanded={historyOpen} aria-label="Past sessions">
        <span>
          <i className="bi bi-clock-history me-2" aria-hidden="true" style={{ marginRight: 8 }} />
          Past sessions
        </span>
        <i className={`bi ${historyOpen ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
      </Button>
      {historyOpen && (
        <Box sx={{ display: "grid", gap: 1 }}>
          {historyLoading ? (
            <Box className="small muted">Loading…</Box>
          ) : !history?.length ? (
            <Box className="small muted">No past sessions yet.</Box>
          ) : (
            history.map((h) => (
              <Box
                key={h.id}
                className="d-flex align-items-center gap-2"
                sx={{
                  p: 1,
                  borderRadius: "var(--radius-sm, 12px)",
                  border: "1px solid var(--surface-border)",
                  background: "var(--surface-soft)",
                }}
              >
                <HistoryThumb entry={h} />
                <Box className="min-w-0 flex-grow-1">
                  <Box className="small fw-semibold">With {h.peerName}</Box>
                  <Box className="small muted">
                    {formatRelativeTime(h.endedAt)} &middot; {formatDuration(h.startedAt, h.endedAt)}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
